import { useReactiveVar } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { mdiSchool } from '@mdi/js';

import { Icon } from 'common/components/Icon';
import { trainingStudentView } from 'training/state/trainingStudentView';
import { IExternalContentModuleListItemFragment } from 'graphql/types';
import { TTrainingModuleProps } from 'training/types';
import { ModuleConditionsChips } from 'training/components/ModuleConditionsChips';
import {
    ListItem,
    ListItemMedia,
    ListItemText,
} from 'common/components/ListItem';
import { Link } from 'common/components/Link';
import { ModuleSecondaryAction } from 'training/components/ModuleSecondaryAction';

export const ExternalContentModule = ({
    id,
    trainingId,
    title,
    description,
    content,
    disabled,
    conditions,
    alwaysOpen,
    startDate,
    image,
    conditionEffect,
    groupConditionEffect,
    weight,
    canUpdate,
    portfolioItem,
    hasProgress,
}: TTrainingModuleProps<IExternalContentModuleListItemFragment>) => {
    const [translate] = useTranslation();
    const studentView = useReactiveVar(trainingStudentView);

    const { link } = content;

    const itemContent = (
        <>
            <ListItemMedia
                color="primary"
                image={image?.url || content.imageUrl || undefined}
            >
                <Icon path={mdiSchool} size="1.8rem" sizeSm="3rem" />
            </ListItemMedia>
            <ListItemText
                primary={title || content.title}
                secondary={description}
            >
                <ModuleConditionsChips
                    alwaysOpen={alwaysOpen}
                    conditionEffect={conditionEffect}
                    conditions={conditions}
                    groupConditionEffect={groupConditionEffect}
                    startDate={startDate}
                />
            </ListItemText>
        </>
    );

    if (disabled) return <ListItem disabled>{itemContent}</ListItem>;

    if (!studentView && canUpdate) {
        return (
            <ListItem
                button
                component={Link}
                params={{
                    trainingId,
                    moduleId: id,
                    type: 'externalContent',
                }}
                to="TRAINING_UPDATE_MODULE"
            >
                {itemContent}

                <ModuleSecondaryAction
                    forUpdate
                    hasProgress={!!hasProgress}
                    portfolioItem={portfolioItem}
                    weight={weight}
                />
            </ListItem>
        );
    }

    return (
        <ListItem button component={Link} href={link} target="_blank">
            {itemContent}

            <ModuleSecondaryAction
                actionText={translate('open')}
                hasProgress={!!hasProgress}
                portfolioItem={portfolioItem}
                weight={weight}
            />
        </ListItem>
    );
};
